import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import BlockAccordion from "@components/accordions/BlockAccordion";
import styles from "@components/carePlan/CarePlan.module.css";
import SessionHistory from "@components/carePlan/SessionHistory";
import ClientCareProfileForm from "@components/forms/ClientCareProfileForm";
import ClientNeedsForm from "@components/forms/ClientNeedsForm";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useOpener from "@hooks/useOpener";
import { CarePlanSectionEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery } from "@typing/Generated";
import { Direction } from "@typing/Models";
import { imageUrl } from "@utils/imageUtils";
import { getCareProfileName } from "@utils/modelUtils";
import { isMobile } from "@utils/platformUtils";

type Props = {
  handleCurrentSectionChange: (section: CarePlanSectionEnum, direction: Direction) => void;
  journey: GetJourneyWithPlanQuery["journey"];
};

type Section = "profile" | "needs" | "sessionHistory";

const Profile = ({ journey }: Props) => {
  const intl = useIntl();

  const queryParams = new URLSearchParams(window.location.search);
  const profileSection = queryParams.get("profileSection") as Section | null;
  const consultId = queryParams.get("consultId");

  const [selectedSection, setSelectedSection] = useState<Section | null>(profileSection);
  const opener = useOpener(isMobile() && !!profileSection);

  return (
    <div>
      <div className={`${styles.formMaxWidth} ion-hide-lg-down`}>
        <BlockAccordion
          border={false}
          color="white"
          content={<ClientCareProfileForm autofocus={false} journey={journey} />}
          defaultOpen={profileSection === "profile" || profileSection === null}
          heading={
            <div className="flex items-center">
              <div className="ion-margin-end margin-4-top">
                <Icon color="secondary" icon="user" />
              </div>
              <Heading2 color="black">
                <FormattedMessage id="components.CarePlan.sections.profile.titleLovedOne" />
              </Heading2>
            </div>
          }
        />
        <div className="margin-20-top">
          <BlockAccordion
            border={false}
            color="white"
            content={<ClientNeedsForm journey={journey} />}
            defaultOpen={profileSection === "needs"}
            heading={
              <div className="flex items-center">
                <div className="ion-margin-end margin-4-top">
                  <Icon color="secondary" icon="infoCircle" />
                </div>
                <Heading2 color="black">
                  <FormattedMessage id="components.CarePlan.sections.profile.titleNeeds" />
                </Heading2>
              </div>
            }
          />
        </div>
        <div className="margin-20-top">
          <BlockAccordion
            border={false}
            color="white"
            content={<SessionHistory editingConsultId={consultId} journey={journey} />}
            defaultOpen={profileSection === "sessionHistory"}
            heading={
              <div className="flex items-center">
                <div className="ion-margin-end margin-4-top">
                  <Icon color="secondary" icon="calendarCheck" />
                </div>
                <Heading2 color="black">
                  <FormattedMessage id="components.CarePlan.sections.profile.sessionHistory" />
                </Heading2>
              </div>
            }
          />
        </div>
      </div>
      <div className="ion-hide-lg-up">
        <div className={`align-center ${styles.avatarContainer}`}>
          <img
            alt={intl.formatMessage({ id: "accessibility.altTags.avatar" })}
            className={styles.avatar}
            src={imageUrl("avatar")}
          />
          <div className="margin-10-top">
            <Heading2 color="gray">{getCareProfileName({ intl, journey })}</Heading2>
          </div>
        </div>
        <div className="ion-margin-start margin-20-bottom">
          <ButtonLink
            onClick={() => {
              setSelectedSection("profile");
              opener.open();
            }}
            stylized={false}
          >
            <div className="flex items-center">
              <div className="ion-margin-end margin-4-top">
                <Icon color="secondary" icon="user" />
              </div>
              <Heading2 color="black">
                <FormattedMessage id="components.CarePlan.sections.profile.titleLovedOne" />
              </Heading2>
            </div>
          </ButtonLink>
        </div>

        <div className="ion-margin-start margin-20-bottom">
          <ButtonLink
            onClick={() => {
              setSelectedSection("needs");
              opener.open();
            }}
            stylized={false}
          >
            <div className="flex items-center">
              <div className="ion-margin-end margin-4-top">
                <Icon color="secondary" icon="infoCircle" />
              </div>
              <Heading2 color="black">
                <FormattedMessage id="components.CarePlan.sections.profile.titleNeeds" />
              </Heading2>
            </div>
          </ButtonLink>
        </div>
        <div className="ion-margin-start margin-20-bottom">
          <ButtonLink
            onClick={() => {
              setSelectedSection("sessionHistory");
              opener.open();
            }}
            stylized={false}
          >
            <div className="flex items-center">
              <div className="ion-margin-end margin-4-top">
                <Icon color="secondary" icon="calendarCheck" />
              </div>
              <Heading2 color="black">
                <FormattedMessage id="components.CarePlan.sections.profile.sessionHistory" />
              </Heading2>
            </div>
          </ButtonLink>
        </div>
      </div>
      <Modal
        opener={opener}
        title={
          selectedSection === "profile"
            ? intl.formatMessage({ id: "components.CarePlan.sections.profile.titleLovedOne" })
            : selectedSection === "needs"
              ? intl.formatMessage({ id: "components.CarePlan.sections.profile.titleNeeds" })
              : intl.formatMessage({ id: "components.CarePlan.sections.profile.sessionHistory" })
        }
      >
        {() => (
          <div>
            <div className="ion-margin-top">
              {selectedSection === "profile" && <ClientCareProfileForm autofocus={false} journey={journey} />}
              {selectedSection === "needs" && <ClientNeedsForm journey={journey} />}
              {selectedSection === "sessionHistory" && (
                <SessionHistory editingConsultId={consultId} journey={journey} />
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Profile;
